import { useState, useCallback } from "react";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { lighten, darken } from "@mui/system";
import {
  Typography,
  Box,
  Avatar,
  Collapse,
  TextField,
  Paper,
  IconButton,
  FormLabel,
  Chip,
  Alert,
  InputAdornment,
  Tooltip,
  Zoom,
  Fade,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { FaEdit } from "react-icons/fa";
import { useTheme, alpha } from "@mui/material/styles";
import AccountTreeTwoToneIcon from "@mui/icons-material/AccountTreeTwoTone";
import {
  Schedule,
  Description as DescIcon,
  CheckCircle,
  Info as InfoIcon,
} from "@mui/icons-material";
import { MyFormData } from "./form-context";
import { getSingleSelectStyles, getMultiSelectStyles } from "@/lib/styles";
import { useTranslation } from "react-i18next";

const animatedComponents = makeAnimated();

const PermitInfo = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const singleSelectStyles = getSingleSelectStyles(theme);
  const multiSelectStyles = getMultiSelectStyles(theme);

  const isDarkMode = theme.palette.mode === "dark";

  const {
    ptwData,
    setPtwData,
    handleWorkflowSelect,
    handleSingleSelect,
    handleMultiSelect,
    changeHandlerText,
    errorTime,
    SitesList,
    UsersList,
    WorkFlowList,
  } = MyFormData();

  const [isSelecting, setIsSelecting] = useState(!ptwData.work_flow_id?.value);
  const [siteFocused, setSiteFocused] = useState(false);
  const [descriptionFocused, setDescriptionFocused] = useState(false);
  const [titleFocused, setTitleFocused] = useState(false);
  const [dateFocused, setDateFocused] = useState(false);
  const [performerFocused, setPerformerFocused] = useState(false);

  const onChangeWorkflow = useCallback(() => {
    setIsSelecting(true);
    setPtwData((prev) => ({
      ...prev,
      work_flow_id: "",
      stepInputsData: {},
    }));
    localStorage.removeItem("step_inputs");
  }, [setPtwData]);

  const onPickWorkflow = (wfOption) => {
    handleWorkflowSelect(wfOption);
    setIsSelecting(false);
  };

  const workflowSelected = Boolean(ptwData.work_flow_id?.value);

  const getFieldValidation = (field, value, focused) => {
    if (!focused) return { isValid: true, message: "" };

    const validationMap = {
      title: {
        isValid: !!(typeof value === "string" && value.trim()),
        message:
          typeof value === "string" && value.trim()
            ? ""
            : t("permitInfo.errorTitleRequired"),
      },
      site_id: {
        isValid: !!value?.value,
        message: value?.value ? "" : t("permitInfo.errorSiteRequired"),
      },
      description: {
        isValid: !!(typeof value === "string" && value.trim()),
        message:
          typeof value === "string" && value.trim()
            ? ""
            : t("permitInfo.errorDescriptionRequired"),
      },
      start_datetime: {
        isValid: !!value && !errorTime,
        message: !value
          ? t("permitInfo.errorStartTimeRequired")
          : errorTime || "",
      },
      performing_user_ids: {
        isValid: Array.isArray(value) && value.length > 0,
        message:
          Array.isArray(value) && value.length > 0
            ? ""
            : t("permitInfo.errorTeamMembersRequired"),
      },
    };

    return validationMap[field] || { isValid: true, message: "" };
  };

  const getTextFieldStyles = (isValid, focused) => ({
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor:
          !isValid && focused
            ? theme.palette.error.main
            : focused
              ? theme.palette.primary.main
              : alpha(theme.palette.text.primary, 0.2),
        borderWidth: focused ? 2 : 1,
        transition: "all 0.3s ease",
      },
      "&:hover fieldset": {
        borderColor:
          !isValid && focused
            ? theme.palette.error.main
            : theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: !isValid
          ? theme.palette.error.main
          : theme.palette.primary.main,
      },
      backgroundColor:
        !isValid && focused
          ? alpha(theme.palette.error.main, 0.05)
          : focused
            ? alpha(theme.palette.primary.main, 0.03)
            : "transparent",
    },
    transition: "all 0.3s ease",
  });

  return (
    <Box>
      {isSelecting ? (
        <Fade in timeout={500}>
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.info.main, 0.1),
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              }}
            >
              <InfoIcon color="info" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("permitInfo.selectWorkType")}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)", // 2 items per row on mobile
                  sm: "repeat(3, 1fr)", // 3 items per row on tablet
                  md: "repeat(4, 1fr)", // 4 items per row on small desktop
                  lg: "repeat(6, 1fr)", // 6 items per row on large desktop
                },
                gap: { xs: 1.5, sm: 2, md: 2.5 },
                justifyItems: "center",
                mt: 1.5,
                mx: "auto", // Center the grid
                maxWidth: { xs: "100%", sm: "100%", md: "900px", lg: "1200px" },
              }}
            >
              {WorkFlowList.map((wf) => {
                const isActive = ptwData.work_flow_id?.value === wf.value;
                return (
                  <Zoom
                    in
                    timeout={500}
                    style={{
                      transitionDelay: `${WorkFlowList.indexOf(wf) * 100}ms`,
                    }}
                    key={wf.value}
                  >
                    <Paper
                      onClick={() => onPickWorkflow(wf)}
                      elevation={isActive ? 8 : 2}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        padding: { xs: 1.5, sm: 2, md: 2.5 },
                        borderRadius: 4,
                        cursor: "pointer",
                        textAlign: "center",
                        backgroundColor: isActive
                          ? alpha(theme.palette.primary.main, 0.1)
                          : theme.palette.background.paper,
                        border: `3px solid ${
                          isActive
                            ? theme.palette.primary.main
                            : alpha(theme.palette.text.primary, 0.1)
                        }`,
                        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                        transform: isActive ? "scale(1.05)" : "scale(1)",
                        boxShadow: isActive
                          ? `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
                          : `0 4px 12px ${alpha(theme.palette.text.primary, 0.1)}`,
                        "&:hover": {
                          transform: "scale(1.08)",
                          backgroundColor: isActive
                            ? alpha(theme.palette.primary.main, 0.15)
                            : alpha(theme.palette.primary.main, 0.05),
                          borderColor: theme.palette.primary.main,
                          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                        },
                        width: { xs: "120px", sm: "130px", md: "140px" },
                        height: { xs: "140px", sm: "150px", md: "160px" },
                        position: "relative",
                        overflow: "hidden",
                        "&::before": isActive
                          ? {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              height: 4,
                              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                              borderRadius: "16px 16px 0 0",
                            }
                          : {},
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          gap: { xs: 1, sm: 1.5 },
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: isActive
                              ? theme.palette.primary.main
                              : alpha(theme.palette.text.primary, 0.1),
                            color: isActive
                              ? theme.palette.primary.contrastText
                              : theme.palette.text.primary,
                            width: { xs: 36, sm: 40, md: 44 },
                            height: { xs: 36, sm: 40, md: 44 },
                            transition: "all 0.3s ease",
                            boxShadow: isActive
                              ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                              : "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <AccountTreeTwoToneIcon
                            sx={{
                              fontSize: {
                                xs: "1.25rem",
                                sm: "1.5rem",
                                md: "1.75rem",
                              },
                            }}
                          />
                        </Avatar>
                        <Typography
                          variant="body2"
                          align="center"
                          sx={{
                            maxWidth: { xs: 100, sm: 110 },
                            userSelect: "none",
                            fontSize: {
                              xs: "0.7rem",
                              sm: "0.75rem",
                              md: "0.8rem",
                            },
                            fontWeight: isActive ? 600 : 500,
                            color: isActive
                              ? theme.palette.primary.main
                              : theme.palette.text.primary,
                            transition: "color 0.3s ease",
                            lineHeight: 1.3,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {wf.label.replaceAll("-", " ")}
                        </Typography>
                        {isActive && (
                          <Chip
                            icon={
                              <CheckCircle
                                sx={{
                                  fontSize: { xs: "0.875rem", sm: "1rem" },
                                }}
                              />
                            }
                            label={t("permitInfo.selectedLabel")}
                            size="small"
                            color="primary"
                            sx={{
                              fontSize: { xs: "0.65rem", sm: "0.7rem" },
                              height: { xs: 18, sm: 20 },
                              "& .MuiChip-label": {
                                px: { xs: 1, sm: 2 },
                              },
                            }}
                          />
                        )}
                      </Box>
                    </Paper>
                  </Zoom>
                );
              })}
            </Box>
          </Box>
        </Fade>
      ) : (
        <Fade in timeout={300}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.success.main, 0.1),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            }}
          >
            <CheckCircle color="success" />
            <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
              {ptwData.work_flow_id?.label}
            </Typography>
            <Tooltip title={t("permitInfo.changeWorkflowTooltip")}>
              <IconButton
                color="primary"
                onClick={onChangeWorkflow}
                aria-label={t("permitInfo.editWorkflowAria")}
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <FaEdit size={"18px"} />
              </IconButton>
            </Tooltip>
          </Box>
        </Fade>
      )}

      <Collapse in={workflowSelected && !isSelecting} timeout={500}>
        <Box sx={{ mt: 1.5 }}>
          <Fade in timeout={600}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                mb: 2,
                borderRadius: 3,
                backgroundColor: isDarkMode
                  ? darken(theme.palette.background.paper, 0.02)
                  : lighten(theme.palette.background.paper, 0.5),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                },
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
              >
                <DescIcon color="primary" />
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                  }}
                >
                  {t("permitInfo.workDetailsSection")}
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormLabel
                    required
                    sx={{
                      color: theme.palette.text.primary,
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      "&::after": {
                        color: theme.palette.error.main,
                        ml: 0.5,
                      },
                    }}
                  >
                    {t("permitInfo.titleLabel")}
                  </FormLabel>
                  <TextField
                    placeholder={t("permitInfo.titlePlaceholder")}
                    fullWidth
                    value={ptwData.title || ""}
                    onChange={(e) => changeHandlerText(e.target, "title")}
                    onBlur={() => setTitleFocused(true)}
                    sx={getTextFieldStyles(
                      getFieldValidation("title", ptwData.title, titleFocused)
                        .isValid,
                      titleFocused
                    )}
                    aria-label={t("permitInfo.titleAria")}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <DescIcon color="primary" fontSize="small" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  {titleFocused &&
                    !getFieldValidation("title", ptwData.title, titleFocused)
                      .isValid && (
                      <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                        <Typography variant="body2">
                          {
                            getFieldValidation(
                              "title",
                              ptwData.title,
                              titleFocused
                            ).message
                          }
                        </Typography>
                      </Alert>
                    )}
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormLabel
                    required
                    sx={{
                      color: theme.palette.text.primary,
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      "&::after": {
                        color: theme.palette.error.main,
                        ml: 0.5,
                      },
                    }}
                  >
                    {t("permitInfo.siteLabel")}
                  </FormLabel>
                  <Select
                    placeholder={t("permitInfo.sitePlaceholder")}
                    options={SitesList}
                    closeMenuOnSelect={true}
                    value={ptwData?.site_id}
                    onBlur={() => setSiteFocused(true)}
                    onChange={handleSingleSelect("site_id")}
                    styles={{
                      ...singleSelectStyles,
                    }}
                    menuPosition="fixed"
                    menuShouldScrollIntoView={false}
                    aria-label={t("permitInfo.siteAria")}
                    components={animatedComponents}
                  />
                  {siteFocused &&
                    !getFieldValidation("site_id", ptwData.site_id, siteFocused)
                      .isValid && (
                      <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                        <Typography variant="body2">
                          {
                            getFieldValidation(
                              "site_id",
                              ptwData.site_id,
                              siteFocused
                            ).message
                          }
                        </Typography>
                      </Alert>
                    )}
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormLabel
                    required
                    sx={{
                      color: theme.palette.text.primary,
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      "&::after": {
                        color: theme.palette.error.main,
                        ml: 0.5,
                      },
                    }}
                  >
                    {t("permitInfo.startTimeLabel")}
                  </FormLabel>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    name="work_date"
                    value={ptwData.start_datetime || ""}
                    onBlur={() => setDateFocused(true)}
                    onChange={(e) =>
                      changeHandlerText(e.target, "start_datetime")
                    }
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Schedule color="primary" fontSize="small" />
                          </InputAdornment>
                        ),
                      },
                      inputLabel: { shrink: true },
                    }}
                    sx={getTextFieldStyles(
                      getFieldValidation(
                        "start_datetime",
                        ptwData.start_datetime,
                        dateFocused
                      ).isValid,
                      dateFocused
                    )}
                    aria-label={t("permitInfo.startTimeAria")}
                  />
                  {(errorTime || (dateFocused && !ptwData.start_datetime)) && (
                    <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                      <Typography variant="body2">
                        {errorTime ||
                          getFieldValidation(
                            "start_datetime",
                            ptwData.start_datetime,
                            dateFocused
                          ).message}
                      </Typography>
                    </Alert>
                  )}
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormLabel
                    required
                    sx={{
                      color: theme.palette.text.primary,
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      "&::after": {
                        color: theme.palette.error.main,
                        ml: 0.5,
                      },
                    }}
                  >
                    {t("permitInfo.teamMembersLabel")}
                  </FormLabel>
                  <Select
                    required
                    placeholder={t("permitInfo.teamMembersPlaceholder")}
                    options={UsersList}
                    components={animatedComponents}
                    isMulti
                    closeMenuOnSelect={false}
                    blurInputOnSelect={false}
                    value={ptwData?.performing_user_ids}
                    onChange={handleMultiSelect("performing_user_ids")}
                    onBlur={() => setPerformerFocused(true)}
                    styles={{
                      ...multiSelectStyles,
                      menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                      control: (provided, state) => ({
                        ...provided,
                        ...multiSelectStyles.control(provided, state),
                        borderColor:
                          !getFieldValidation(
                            "performing_user_ids",
                            ptwData.performing_user_ids,
                            performerFocused
                          ).isValid && performerFocused
                            ? theme.palette.error.main
                            : state.isFocused
                              ? theme.palette.secondary.main
                              : alpha(theme.palette.text.primary, 0.2),
                        borderWidth: state.isFocused ? 2 : 1,
                      }),
                    }}
                    menuPosition="fixed"
                    menuShouldScrollIntoView={false}
                    aria-label={t("permitInfo.teamMembersAria")}
                  />
                  {performerFocused &&
                    !getFieldValidation(
                      "performing_user_ids",
                      ptwData.performing_user_ids,
                      performerFocused
                    ).isValid && (
                      <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                        <Typography variant="body2">
                          {
                            getFieldValidation(
                              "performing_user_ids",
                              ptwData.performing_user_ids,
                              performerFocused
                            ).message
                          }
                        </Typography>
                      </Alert>
                    )}
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormLabel
                    required
                    sx={{
                      color: theme.palette.text.primary,
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      "&::after": {
                        color: theme.palette.error.main,
                        ml: 0.5,
                      },
                    }}
                  >
                    {t("permitInfo.descriptionLabel")}
                  </FormLabel>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={ptwData.description || ""}
                    placeholder={t("permitInfo.descriptionPlaceholder")}
                    onBlur={() => setDescriptionFocused(true)}
                    onChange={(e) => changeHandlerText(e.target, "description")}
                    sx={getTextFieldStyles(
                      getFieldValidation(
                        "description",
                        ptwData.description,
                        descriptionFocused
                      ).isValid,
                      descriptionFocused
                    )}
                    aria-label={t("permitInfo.descriptionAria")}
                  />
                  {descriptionFocused &&
                    !getFieldValidation(
                      "description",
                      ptwData.description,
                      descriptionFocused
                    ).isValid && (
                      <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                        <Typography variant="body2">
                          {
                            getFieldValidation(
                              "description",
                              ptwData.description,
                              descriptionFocused
                            ).message
                          }
                        </Typography>
                      </Alert>
                    )}
                </Grid>
              </Grid>
            </Paper>
          </Fade>
        </Box>
      </Collapse>
    </Box>
  );
};

export default PermitInfo;
